import React, { useMemo } from 'react';
import { useAtom } from 'jotai';
import { Checkbox } from '@carbon/react';
import {
  typeCurveWellsVisibilityAtom,
  ltdPropertiesVisibilityAtom,
  typeCurveUnitsVisibilityAtom,
  typeCurveAreaVisibilityAtom,
} from './atoms';
import style from 'components/map/map.module.scss';

/**
 * The map's "Type Curve" layer group.
 *
 * One parent with three children — the wells, the LTD laterals and the unit
 * polygons. The parent is a roll-up: checking it shows all three, and it reads
 * indeterminate while they disagree.
 *
 * This replaced a "Type Curve Wells" group whose children were the three well
 * categories (Well Control / Analog Wells / New Wells). Those are no longer
 * separately toggleable, so the wells layer reads one atom for all of them.
 *
 * Shared by every surface that renders `TypeCurveMap` — Type Curve details, the
 * Type Curve workspace and the Inventory Workspace.
 *
 * @return {JSX.Element} The group.
 */
const ScopedWellsLayerSelection = () => {
  const [wellsVisible, setWellsVisible] = useAtom(typeCurveWellsVisibilityAtom);
  const [ltdVisible, setLtdVisible] = useAtom(ltdPropertiesVisibilityAtom);
  const [unitsVisible, setUnitsVisible] = useAtom(typeCurveUnitsVisibilityAtom);
  const [areaVisible, setAreaVisible] = useAtom(typeCurveAreaVisibilityAtom);

  // Derived rather than mirrored into state: the previous version kept the
  // roll-up in `useState` and synced it in an effect, which meant the parent
  // lagged its children by a render.
  const { allVisible, indeterminate } = useMemo(() => {
    const flags = [wellsVisible, ltdVisible, unitsVisible, areaVisible];
    const every = flags.every(Boolean);
    return {
      allVisible: every,
      indeterminate: !every && flags.some(Boolean),
    };
  }, [wellsVisible, ltdVisible, unitsVisible, areaVisible]);

  const setAll = (checked: boolean) => {
    setWellsVisible(checked);
    setLtdVisible(checked);
    setUnitsVisible(checked);
    setAreaVisible(checked);
  };

  // `swatch` carries the colour chip: the panel draws it from a per-layer class
  // on the wrapper, so a child without one falls back to the generic swatch.
  const children: Array<{
    id: string;
    label: string;
    swatch: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
  }> = [
    {
      id: 'type-curve-wells-checkbox',
      label: 'Type Curve Wells',
      swatch: style.typeCurveWellsCheck,
      checked: wellsVisible,
      onChange: setWellsVisible,
    },
    {
      id: 'ltd-properties-checkbox',
      label: 'LTD Properties',
      swatch: style.ltdPropertiesCheck,
      checked: ltdVisible,
      onChange: setLtdVisible,
    },
    {
      id: 'type-curve-units-checkbox',
      label: 'Type Curve Units',
      swatch: style.typeCurveUnitsCheck,
      checked: unitsVisible,
      onChange: setUnitsVisible,
    },
    {
      id: 'type-curve-area-checkbox',
      label: 'Type Curve Area',
      swatch: style.typeCurveAreaCheck,
      checked: areaVisible,
      onChange: setAreaVisible,
    },
  ];

  return (
    <>
      <div className={style.titleArea}>
        <div className={style.allCheckBox}>
          <Checkbox
            id="typeCurveAll"
            labelText="Type Curve"
            checked={allVisible}
            indeterminate={indeterminate}
            onChange={(
              _evt: React.ChangeEvent<HTMLInputElement>,
              { checked }: { checked: boolean }
            ) => setAll(checked)}
          />
        </div>
      </div>
      <div className={style.groupChecks}>
        {children.map((child) => (
          <div key={child.id} className={child.swatch}>
            <Checkbox
              id={child.id}
              labelText={child.label}
              checked={child.checked}
              onChange={(
                _evt: React.ChangeEvent<HTMLInputElement>,
                { checked }: { checked: boolean }
              ) => child.onChange(checked)}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export { ScopedWellsLayerSelection };
